// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.

import { createPageElement } from "@insite/client-framework/Components/ContentItemStore";
import { ShellContext } from "@insite/client-framework/Components/IsInShell";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentAccountState } from "@insite/client-framework/Store/Data/Accounts/AccountsSelector";
import loadAccount from "@insite/client-framework/Store/Data/Accounts/Handlers/LoadAccount";
import { loadPageByType } from "@insite/client-framework/Store/Data/Pages/PagesActionCreators";
import { getPageStateByType } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import Modal from "@insite/mobius/Modal";
import closeJobListsInfoPopup from "@plumberssupply/App/Handlers/MyLists/Handlers/CloseJobListsInfoPopup";
import { parseBoolean, xIsAuthenticated } from "@plumberssupply/XDL/Utilities";
import * as React from "react";
import { FC, useContext, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: ApplicationState) => {
    // eslint-disable-next-line camelcase
    const pls_seenJobListsAnnouncement = state.context.session.properties.pls_seenJobListsAnnouncement;
    return {
        modalIsOpen:
            // eslint-disable-next-line camelcase
            !!pls_seenJobListsAnnouncement && !parseBoolean(pls_seenJobListsAnnouncement) && xIsAuthenticated(state),
        page: getPageStateByType(state, "JobListsInfoPopup"),
        shouldLoadAccount: !getCurrentAccountState(state).value,
    };
};

const mapDispatchToProps = {
    loadPageByType,
    closeJobListsInfoPopup,
    loadAccount,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const NewJobListsModal: FC<Props> = ({
    modalIsOpen,
    closeJobListsInfoPopup,
    page,
    loadPageByType,
    shouldLoadAccount,
    loadAccount,
}) => {
    const { isInShell } = useContext(ShellContext);
    useEffect(() => {
        if (shouldLoadAccount) {
            loadAccount({ accountId: API_URL_CURRENT_FRAGMENT });
        }
    });
    useEffect(() => {
        if (!page.value && !page.isLoading) {
            loadPageByType("JobListsInfoPopup");
        }
    }, [page.isLoading, page.value, loadPageByType]);

    const modalCloseHandler = () => {
        closeJobListsInfoPopup();
    };

    if (!page.value || isInShell || shouldLoadAccount) {
        return null;
    }

    return (
        <Modal headline={page.value.fields["modalTitle"]} isOpen={modalIsOpen} handleClose={modalCloseHandler}>
            <div data-test-selector="jobListsInfoPopup">
                <ShellContext.Provider value={{ isInShell, pageId: page.id }}>
                    {createPageElement(page.value.type, page.value)}
                </ShellContext.Provider>
            </div>
        </Modal>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(NewJobListsModal);
